// src/components/SuperAdmin/IndustryProfileManager.jsx
// Task 11 / GOV-7 — SuperAdmin governance surface for declarative Industry Profiles (db.industry_profiles).
// Renders the canonical backend authority (never label-matching); actions call the SuperAdmin-only routes.
// No provider secrets are ever rendered. Location/ETA state is shown truthfully (provider not configured).

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const IndustryProfileManager = () => {
  const { superAdmin } = useSuperAdminAuth();
  const { toast } = useToast();
  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdmin?.token || localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') || localStorage.getItem('superAdminToken');
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [mapForm, setMapForm] = useState({}); // per-profile PENDING edit { industry_id, sub_sector_ids }
  const [industries, setIndustries] = useState([]);
  const [subSectors, setSubSectors] = useState([]);

  // Generic Create-Profile authoring (distinct from the version editor below). Lands a brand-new
  // DRAFT profile through the SAME canonical create route (POST /industry-profiles); it never
  // versions or mutates an existing profile.
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    profile_key: '', display_name: '', enabled_engines: [],
    settings_roles: [], usage_roles: [], confirm_roles: [], confirm_scope: 'assigned',
  });

  const arr = (v) => (Array.isArray(v) ? v : []);
  // Show the governed NAME for a persisted mapping; fall back to the raw id only when the
  // taxonomy has not loaded, so the surface is never blank and never invents a label.
  const nameOfIndustry = (id) => {
    if (!id) return '— (unassigned)';
    return (industries.find((i) => i.id === id) || {}).name || id;
  };
  const namesOfSubs = (ids) => {
    const list = arr(ids);
    if (!list.length) return '— (all)';
    return list.map((id) => (subSectors.find((sx) => sx.id === id) || {}).name || id).join(', ');
  };
  const active = (p) => p?.lifecycle === 'active';

  // GOV-7 E-5 — safe human labels for the governed location-source enums (never render the raw enum/id).
  const SOURCE_LABELS = {
    BUSINESS_OPERATOR_PHONE: 'Business operator phone',
    REGISTERED_VEHICLE_TRACKER: 'Registered vehicle tracker',
    TELEMATICS_PROVIDER: 'Telematics provider',
  };
  // Absent policy (v1/v2) = phone-only legacy semantics — a tenant is NEVER silently tracker-capable.
  const allowedSourceLabels = (p) => {
    const raw = arr(p?.location_eta_config?.allowed_location_sources);
    const list = raw.length ? raw : ['BUSINESS_OPERATOR_PHONE'];
    return list.map((s) => SOURCE_LABELS[s] || s).join(', ');
  };

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/v1/superadmin/industry-profiles');
      setProfiles(arr(resp.data?.profiles));
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || 'Could not load industry profiles', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchProfiles(); /* eslint-disable-next-line */ }, []);

  // The governed workflow-stage vocabulary, read from the backend that ENFORCES it. Deliberately not
  // a constant in this file: a client-side copy would drift the moment a state is added to
  // AppointmentStatus and would offer stages the transition rule then refuses. If the read fails the
  // control simply offers nothing to add - it never falls back to a guessed list.
  const [statusVocab, setStatusVocab] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/v1/superadmin/industry-profiles/status-vocabulary');
        setStatusVocab(arr(r.data?.status_vocabulary));
      } catch { setStatusVocab([]); }
    })();
  }, []);

  // Canonical governed taxonomy for the mapping dropdowns. These are the SuperAdmin-MOUNTED taxonomy
  // reads: this surface authenticates with the SuperAdmin OTP session, so the plain Firebase-gated
  // /taxonomy/* routes 401 here (the same GOV-7 lesson Tenant360 records). Read-only - Taxonomy Manager
  // remains the sole taxonomy WRITE authority; nothing here creates or edits a taxonomy node.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [ind, sub] = await Promise.all([
        api.get('/api/v1/superadmin/taxonomy/industries').catch(() => ({ data: [] })),
        api.get('/api/v1/superadmin/taxonomy/sub-sectors').catch(() => ({ data: [] })),
      ]);
      if (!alive) return;
      setIndustries(Array.isArray(ind.data) ? ind.data : arr(ind.data?.industries));
      setSubSectors(Array.isArray(sub.data) ? sub.data : arr(sub.data?.sub_sectors));
    })();
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, []);

  // The mapping controls are a VIEW of the profile's persisted mapping until the owner edits them.
  // Rendering therefore shows a mapped profile's real Industry/Sub-Sector by name and, critically,
  // submits the stored value unchanged when untouched - rendering can never rewrite a mapping.
  const pendingIndustry = (p) => {
    const f = mapForm[p.id] || {};
    return f.industry_id !== undefined ? f.industry_id : (p.industry_id || '');
  };
  const pendingSubs = (p) => {
    const f = mapForm[p.id] || {};
    return f.sub_sector_ids !== undefined ? f.sub_sector_ids : arr(p.sub_sector_ids);
  };
  // Sub-sectors are constrained to the chosen Industry, so an invalid parent/child pair cannot be
  // submitted from the UI at all. Server validation remains authoritative regardless.
  const subOptionsFor = (p) => {
    const ind = pendingIndustry(p);
    return subSectors.filter((sx) => !ind || sx.industry_id === ind);
  };
  const setIndustryFor = (p, industryId) => {
    // Changing Industry drops any sub-sector that does not belong to it - never silently keep a
    // cross-industry pair.
    const keep = pendingSubs(p).filter((id) => {
      const sx = subSectors.find((x) => x.id === id);
      return sx && sx.industry_id === industryId;
    });
    setMapForm({ ...mapForm, [p.id]: { industry_id: industryId, sub_sector_ids: keep } });
  };
  const setSubFor = (p, subId) => {
    setMapForm({ ...mapForm, [p.id]: { industry_id: pendingIndustry(p), sub_sector_ids: subId ? [subId] : [] } });
  };

  // ONE seed handler for every declarative profile. Each canonical profile has an idempotent
  // create-only seed route under the SAME industry-profiles authority; the control differs only by
  // profile key, so a new profile never needs a new page, route, authority or auth path. Seeds land
  // at lifecycle=draft — publishing stays the governed lifecycle transition below.
  const seedProfile = async (key, label) => {
    setBusyId(`seed-${key}`);
    try { await api.post(`/api/v1/superadmin/industry-profiles/seed-${key}`); toast({ title: `${label} profile ready` }); await fetchProfiles(); }
    catch (e) { toast({ title: 'Seed failed', description: e?.response?.data?.detail || 'error', variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  // The ONLY engine identifiers a generic profile may enable — the canonical set the live consumers
  // read (scheduling / location-eta / notification / assignment), matching the seed constants and
  // api/commercial/capabilities.py. The backend does NOT validate enabled_engines, so this form is
  // the guard: it offers exactly these four and nothing else, so a typo can never be persisted. Bare
  // `assignment` (not assignment_engine) is deliberate — it is the real token the consumers read.
  const CREATE_ENGINES = [
    ['scheduling_engine', 'Scheduling'],
    ['location_eta_engine', 'Location / ETA'],
    ['notification_engine', 'Notifications'],
    ['assignment', 'Assignment'],
  ];
  const CREATE_KEY_RE = /^[a-z][a-z0-9_]*$/;  // lowercase canonical key — never a UUID, space or label

  const toggleCreateEngine = (tok) => setCreateForm((f) => {
    const cur = new Set(f.enabled_engines);
    if (cur.has(tok)) cur.delete(tok); else cur.add(tok);
    return { ...f, enabled_engines: CREATE_ENGINES.map(([t]) => t).filter((t) => cur.has(t)) };
  });
  const toggleCreateRole = (field, role) => setCreateForm((f) => {
    const cur = new Set(f[field] || []);
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    return { ...f, [field]: ROLE_VOCAB.filter((r) => cur.has(r)) };
  });

  // Create a NEW draft profile via the canonical create route. This is NOT the version editor: it
  // never touches an existing profile. Per §25.4 it lands lifecycle=draft, so taxonomy binding and
  // activation remain the governed next steps before Tenant360 can discover and assign it.
  const createProfile = async () => {
    const key = (createForm.profile_key || '').trim();
    const name = (createForm.display_name || '').trim();
    if (!CREATE_KEY_RE.test(key)) {
      toast({ title: 'Invalid profile key', description: 'Use lowercase letters, digits and underscores (e.g. tech_services_v1).', variant: 'destructive' });
      return;
    }
    if (!name) { toast({ title: 'Display name required', variant: 'destructive' }); return; }
    if (createForm.enabled_engines.length === 0) {
      toast({ title: 'Select at least one engine', description: 'A profile with no engine provisions nothing.', variant: 'destructive' });
      return;
    }
    const body = { profile_key: key, display_name: name, enabled_engines: createForm.enabled_engines.slice() };
    if (createForm.settings_roles.length || createForm.usage_roles.length || createForm.confirm_roles.length) {
      body.role_policy = {
        settings_roles: createForm.settings_roles.slice(),
        usage_roles: createForm.usage_roles.slice(),
        confirm_roles: createForm.confirm_roles.slice(),
        confirm_scope: createForm.confirm_scope,
      };
    }
    setCreating(true);
    try {
      const r = await api.post('/api/v1/superadmin/industry-profiles', body);
      if (r?.data?.error === 'version_exists') {
        toast({ title: 'Profile key already exists', description: 'A profile with this key already exists. Use a different key, or create a new version from its card.', variant: 'destructive' });
      } else {
        toast({ title: 'Draft profile created', description: `${name} is a DRAFT — bind its Industry/Sub-Sector and Activate it before Tenant360 can assign it.` });
        setCreateForm({ profile_key: '', display_name: '', enabled_engines: [], settings_roles: [], usage_roles: [], confirm_roles: [], confirm_scope: 'assigned' });
        setCreateOpen(false);
        await fetchProfiles();
      }
    } catch (e) {
      toast({ title: 'Create failed', description: e?.response?.data?.detail || e?.response?.data?.error || 'error', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const setLifecycle = async (p, lifecycle) => {
    setBusyId(p.id);
    try { await api.post(`/api/v1/superadmin/industry-profiles/${p.id}/lifecycle`, { lifecycle }); toast({ title: lifecycle === 'active' ? 'Profile activated' : 'Profile suspended', description: 'Suspend stops new eligibility; bookings/history/audit are preserved.' }); await fetchProfiles(); }
    catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || 'error', variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  const assignMapping = async (p) => {
    // The owner selects NAMES; the canonical ids are what travel on the wire. The route, service,
    // schema and audit behaviour are unchanged - only the input mechanism differs.
    const industryId = pendingIndustry(p);
    if (!industryId) { toast({ title: 'Select an Industry', variant: 'destructive' }); return; }
    setBusyId(p.id);
    try {
      await api.post(`/api/v1/superadmin/industry-profiles/${p.id}/mapping`, {
        industry_id: industryId,
        sub_sector_ids: pendingSubs(p),
      });
      toast({ title: 'Mapping assigned' }); await fetchProfiles();
    } catch (e) { toast({ title: 'Mapping failed', description: e?.response?.data?.detail || 'error', variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  // GOVERNED VERSION EDITOR.
  // A published version is IMMUTABLE, so editing publishes the NEXT version through the one canonical
  // authority (PUT /industry-profiles/{id} -> update_profile -> create_new_version); the source document
  // is never written. Generic: renders governed policy for ANY profile and names no industry, profile
  // key or tenant.
  //
  // Only role_policy is patched. Everything else - including industry_id / sub_sector_ids, which the
  // Tenant360 selector filters on - is INHERITED by the backend's clone precisely because we do not send
  // it. Not re-sending is a stronger guarantee than sending and hoping it round-trips.
  const ROLE_VOCAB = ['admin', 'administrator', 'manager', 'operator',
                      'executive', 'analyst', 'it', 'integration', 'director'];
  const CONFIRM_SCOPES = ['assigned', 'any'];   // the only scopes the backend honours
  // The only two values NotificationPolicy.content_privacy accepts. Kept beside CONFIRM_SCOPES for
  // the same reason: offering a third would put a dead option on a governance screen.
  const CONTENT_PRIVACY_VALUES = ['standard', 'sensitive'];

  // CANONICAL capture vocabulary, mirroring the backend schema exactly. These names are the contract:
  // the model ignores unknown keys, so a near-miss like `required_parties` would be DROPPED and the
  // published version would demand consent from nobody — unsatisfiable, and immutable once written.
  // Spelling them once, here, is what keeps the editor from inventing a field the backend discards.
  const CAPTURE_TYPES = ['audio', 'video', 'screen', 'text'];
  const CONSENT_PARTIES = ['subject', 'operator'];
  // The ONLY mode that can ever grant. Rendered as a fixed label rather than a one-option dropdown,
  // for the same reason the lists above are closed: a dead choice on a governance screen is a lie.
  const CONSENT_MODE = 'explicit';

  const [verFor, setVerFor] = useState(null);
  const [verDraft, setVerDraft] = useState(null);
  const [verReview, setVerReview] = useState(false);
  const arrOf = (v) => (Array.isArray(v) ? v.slice() : []);

  const openVersionEditor = (p) => {
    const rp = p.role_policy || {};
    setVerFor(p); setVerReview(false);
    setVerDraft({
      settings_roles: arrOf(rp.settings_roles),
      usage_roles: arrOf(rp.usage_roles),
      confirm_roles: arrOf(rp.confirm_roles),
      confirm_scope: rp.confirm_scope || 'assigned',
      // Seeded from the version being succeeded, so "unchanged" is the default and inheritance is
      // what happens when the owner touches nothing.
      status_workflow: arrOf(p.status_workflow),
      // notification_policy is patched as a WHOLE object by the backend model, so the draft carries
      // its existing `events` alongside the edited field. Sending only the privacy value would
      // validate cleanly and silently publish a successor with an EMPTY event list — the tenant
      // would simply stop being notified, and nothing would report it.
      content_privacy: (p.notification_policy || {}).content_privacy || 'standard',
      notification_events: arrOf((p.notification_policy || {}).events),
      // Seeded from the version being succeeded, like every field above: untouched means inherited.
      resource_display_label: p.resource_display_label || '',
      capture_types: captureDraftFrom(p),
    });
  };
  const closeVersionEditor = () => { setVerFor(null); setVerDraft(null); setVerReview(false); };

  // Read the profile's existing policy into an editable shape. A type the profile never declared
  // starts disabled with no party — absent policy means no capture, and the editor must open on that
  // truth rather than on a helpful-looking default.
  function captureDraftFrom(p) {
    const types = (p && p.capture_policy && p.capture_policy.types) || {};
    const out = {};
    for (const k of CAPTURE_TYPES) {
      const cur = types[k] || null;
      out[k] = {
        enabled: !!(cur && cur.enabled),
        parties: cur ? arrOf(cur.consent_parties) : [],
      };
    }
    return out;
  }

  const toggleCaptureType = (kind) => setVerDraft((d) => ({
    ...d,
    capture_types: { ...d.capture_types,
                     [kind]: { ...d.capture_types[kind], enabled: !d.capture_types[kind].enabled } },
  }));

  const toggleCaptureParty = (kind, party) => setVerDraft((d) => {
    const cur = new Set(d.capture_types[kind].parties);
    if (cur.has(party)) cur.delete(party); else cur.add(party);
    return { ...d, capture_types: { ...d.capture_types,
             [kind]: { ...d.capture_types[kind],
                       parties: CONSENT_PARTIES.filter((x) => cur.has(x)) } } };
  });

  // A human-readable summary used by the diff, so a reviewer compares MEANING rather than JSON.
  const captureSummary = (types) => {
    const on = CAPTURE_TYPES.filter((k) => types[k] && types[k].enabled);
    if (on.length === 0) return 'none declared - no capture permitted';
    return on.map((k) => `${k}: consent ${types[k].parties.join(' + ') || 'UNSATISFIABLE - no party named'}`).join(' | ');
  };

  const captureSummaryFromProfile = (p) => captureSummary(captureDraftFrom(p));

  // THE PAYLOAD. Canonical field names only, and every field the schema declares is written
  // explicitly — including the nulls — so what the owner reviewed is exactly what is stored.
  const capturePolicyPayload = (types) => {
    const out = {};
    for (const k of CAPTURE_TYPES) {
      if (!types[k] || !types[k].enabled) continue;
      out[k] = {
        enabled: true,
        requires_consent: true,
        consent_parties: types[k].parties.slice(),
        consent_mode: CONSENT_MODE,
        consent_ttl_seconds: null,
        authorized_roles: null,
        profile_applicability: null,
      };
    }
    return { types: out };
  };

  const toggleRole = (field, role) => setVerDraft((d) => {
    const cur = new Set(d[field] || []);
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    return { ...d, [field]: ROLE_VOCAB.filter((r) => cur.has(r)) };
  });

  // GENERIC workflow-stage authoring. Order IS the lifecycle: the engine's transition rule is
  // forward-only by INDEX over this list, so moving a stage is a semantic change, not a cosmetic one.
  // Every operation below keeps the list a set: a stage can be added only while absent, so duplicates
  // are impossible by construction as well as refused by the backend.
  const wfDraft = () => arrOf(verDraft?.status_workflow);
  const wfAvailable = () => statusVocab.filter((x) => !wfDraft().includes(x));
  const wfAdd = (status) => setVerDraft((d) => {
    const cur = arrOf(d.status_workflow);
    if (!status || cur.includes(status) || !statusVocab.includes(status)) return d;
    return { ...d, status_workflow: [...cur, status] };
  });
  const wfRemove = (status) => setVerDraft((d) => ({
    ...d, status_workflow: arrOf(d.status_workflow).filter((x) => x !== status) }));
  const wfMove = (i, delta) => setVerDraft((d) => {
    const cur = arrOf(d.status_workflow);
    const j = i + delta;
    if (i < 0 || j < 0 || i >= cur.length || j >= cur.length) return d;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    return { ...d, status_workflow: cur };
  });

  const versionDiff = () => {
    if (!verFor || !verDraft) return [];
    const rp = verFor.role_policy || {};
    const out = [];
    for (const f of ['settings_roles', 'usage_roles', 'confirm_roles']) {
      const before = arrOf(rp[f]).join(', ') || 'none';
      const after = arrOf(verDraft[f]).join(', ') || 'none';
      if (before !== after) out.push({ field: 'role_policy.' + f, before, after });
    }
    const hasConfirm = (verDraft.confirm_roles || []).length > 0;
    const bs = rp.confirm_scope || 'none';
    if (hasConfirm && bs !== verDraft.confirm_scope) {
      out.push({ field: 'role_policy.confirm_scope', before: bs, after: verDraft.confirm_scope });
    }
    const cpBefore = (verFor.notification_policy || {}).content_privacy || 'standard';
    if (cpBefore !== verDraft.content_privacy) {
      out.push({ field: 'notification_policy.content_privacy',
                 before: cpBefore, after: verDraft.content_privacy });
    }
    const rdlBefore = verFor.resource_display_label || 'none';
    const rdlAfter = (verDraft.resource_display_label || '').trim() || 'none';
    if (rdlBefore !== rdlAfter) {
      out.push({ field: 'resource_display_label', before: rdlBefore, after: rdlAfter });
    }
    const capBefore = captureSummaryFromProfile(verFor);
    const capAfter = captureSummary(verDraft.capture_types || {});
    if (capBefore !== capAfter) {
      out.push({ field: 'capture_policy', before: capBefore, after: capAfter });
    }
    const wfBefore = arrOf(verFor.status_workflow).join(' -> ') || 'none';
    const wfAfter = wfDraft().join(' -> ') || 'none';
    if (wfBefore !== wfAfter) out.push({ field: 'status_workflow', before: wfBefore, after: wfAfter });
    return out;
  };

  // What actually changed, so the PATCH carries that and nothing else. The editor's own rule already
  // says not sending a field is a stronger inheritance guarantee than sending it and hoping it round-
  // trips; this applies that rule to role_policy too, which until now was always sent even untouched.
  const versionPatch = () => {
    const changed = versionDiff().map((d) => d.field);
    const patch = {};
    if (changed.some((f) => f.startsWith('role_policy.'))) patch.role_policy = { ...verDraft };
    if (patch.role_policy) delete patch.role_policy.status_workflow;
    if (changed.includes('status_workflow')) patch.status_workflow = wfDraft();
    if (changed.includes('resource_display_label')) {
      patch.resource_display_label = (verDraft.resource_display_label || '').trim();
    }
    if (changed.includes('capture_policy')) {
      patch.capture_policy = capturePolicyPayload(verDraft.capture_types || {});
    }
    if (changed.includes('notification_policy.content_privacy')) {
      // The WHOLE object, with the inherited events carried explicitly.
      patch.notification_policy = { events: arrOf(verDraft.notification_events),
                                    content_privacy: verDraft.content_privacy };
    }
    return patch;
  };

  const createVersion = async () => {
    if (!verFor || !verDraft) return;
    setBusyId(verFor.id);
    try {
      const r = await api.put('/api/v1/superadmin/industry-profiles/' + verFor.id,
        versionPatch());
      if (r?.data?.error === 'version_exists') {
        toast({ title: 'Version already exists',
                description: 'Another version appeared while this editor was open. Refresh and review.',
                variant: 'destructive' });
      } else {
        toast({ title: 'Created v' + (r?.data?.version ?? ''),
                description: verFor.profile_key + ' v' + verFor.version
                             + ' is unchanged; a new version was published.' });
        closeVersionEditor();
      }
      await fetchProfiles();
    } catch (e) {
      toast({ title: 'Create version failed',
              description: e?.response?.data?.detail || 'error', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const roleCell = (p) => {
    const rp = p.role_policy || {};
    const settings = new Set(arr(rp.settings_roles));
    const usage = new Set(arr(rp.usage_roles));
    const label = (r) => settings.has(r) ? 'Settings + Usage' : usage.has(r) ? 'Usage' : 'None';
    return (
      <ul className="text-xs space-y-0.5" data-testid={`roles-${p.id}`}>
        <li>Admin: <b>{label('admin')}</b></li>
        <li>Manager: <b>{label('manager')}</b></li>
        <li>Operator: <b>{label('operator')}</b></li>
        <li>Executive: <b>{label('executive')}</b> · Analyst: <b>{label('analyst')}</b> · IT/Integration: <b>{label('it')}</b> · Director: <b>{label('director')}</b></li>
      </ul>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Industry Profiles</CardTitle>
        <CardDescription>Declarative profiles bind a canonical Industry/Sub-Sector to generic engines (Scheduling · Location/ETA · Notifications · Assignment). SuperAdmin-governed; tenant eligibility = profile ∧ entitlement ∧ role.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Button size="sm" variant="outline" onClick={fetchProfiles} data-testid="ip-refresh">Refresh</Button>
          <Button size="sm" onClick={() => setCreateOpen((v) => !v)} data-testid="ip-create-toggle">{createOpen ? 'Close create' : 'Create Profile'}</Button>
          <Button size="sm" variant="outline" onClick={() => seedProfile('movers', 'Movers')} disabled={busyId === 'seed-movers'} data-testid="ip-seed-movers">Seed Movers profile</Button>
          <Button size="sm" variant="outline" onClick={() => seedProfile('clinic', 'Clinic')} disabled={busyId === 'seed-clinic'} data-testid="ip-seed-clinic">Seed Clinic profile</Button>
        </div>

        {createOpen ? (
          <div className="mb-4 p-3 border rounded bg-gray-50" data-testid="ip-create-panel">
            <div className="text-sm font-semibold mb-1">Create a new industry profile</div>
            <div className="text-[11px] text-gray-500 mb-2">
              Creates a DRAFT profile for an EXISTING canonical Industry/Sub-Sector using the generic
              engines. After creating, bind its Industry/Sub-Sector and Activate it on its card below;
              Tenant360 then discovers and assigns it. This never versions an existing profile.
            </div>
            <div className="flex flex-wrap gap-3 mb-2">
              <label className="text-xs">Profile key
                <input type="text" className="block border rounded px-2 py-1 text-xs w-64 mt-0.5"
                       placeholder="e.g. tech_services_v1" value={createForm.profile_key}
                       onChange={(e) => setCreateForm((f) => ({ ...f, profile_key: e.target.value }))}
                       data-testid="ip-create-key" />
              </label>
              <label className="text-xs">Display name
                <input type="text" className="block border rounded px-2 py-1 text-xs w-64 mt-0.5"
                       placeholder="e.g. Technology Services" value={createForm.display_name}
                       onChange={(e) => setCreateForm((f) => ({ ...f, display_name: e.target.value }))}
                       data-testid="ip-create-name" />
              </label>
            </div>
            <div className="mb-2">
              <div className="text-xs uppercase text-gray-500">engines</div>
              <div className="flex flex-wrap gap-3 mt-1">
                {CREATE_ENGINES.map(([tok, label]) => (
                  <label key={tok} className="text-xs flex items-center gap-1">
                    <input type="checkbox" checked={createForm.enabled_engines.includes(tok)}
                           onChange={() => toggleCreateEngine(tok)}
                           data-testid={`ip-create-engine-${tok}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            {['settings_roles', 'usage_roles', 'confirm_roles'].map((field) => (
              <div key={field} className="mb-2">
                <div className="text-xs uppercase text-gray-500">{field.replace('_', ' ')}</div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ROLE_VOCAB.map((r) => (
                    <label key={r} className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={(createForm[field] || []).includes(r)}
                             onChange={() => toggleCreateRole(field, r)}
                             data-testid={`ip-create-${field}-${r}`} />
                      {r}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="mb-2">
              <div className="text-xs uppercase text-gray-500">confirm scope</div>
              <select className="border rounded px-2 py-1 text-xs mt-1" value={createForm.confirm_scope}
                      onChange={(e) => setCreateForm((f) => ({ ...f, confirm_scope: e.target.value }))}
                      data-testid="ip-create-scope">
                {CONFIRM_SCOPES.map((sc) => <option key={sc} value={sc}>{sc}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" disabled={creating} onClick={createProfile} data-testid="ip-create-submit">
                {creating ? 'Creating…' : 'Create draft profile'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setCreateOpen(false)} data-testid="ip-create-cancel">Cancel</Button>
            </div>
          </div>
        ) : null}

        {loading && <p className="text-sm text-gray-600">Loading…</p>}
        {!loading && profiles.length === 0 && <p className="text-sm text-gray-600" data-testid="ip-empty">No industry profiles yet — seed a declarative profile to begin.</p>}

        <div className="space-y-4">
          {profiles.map((p) => (
            <div key={p.id} className="border rounded-lg p-4" data-testid={`ip-card-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h4 className="font-semibold text-gray-900">{p.display_name}</h4>
                <Badge variant="outline">v{p.version}</Badge>
                <Badge variant={active(p) ? 'outline' : 'destructive'} data-testid={`ip-state-${p.id}`}>{active(p) ? 'Active' : (p.lifecycle || 'draft')}</Badge>
                <Badge variant="outline">{p.profile_key}</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-500 text-xs uppercase">Applicability</div>
                  <div>Industry: <b>{nameOfIndustry(p.industry_id)}</b></div>
                  <div>Sub-Sectors: <b>{namesOfSubs(p.sub_sector_ids)}</b></div>
                  <div className="mt-2 text-gray-500 text-xs uppercase">Engines</div>
                  <div>{arr(p.enabled_engines).join(', ') || '—'}</div>
                  <div className="mt-2 text-gray-500 text-xs uppercase">Booking</div>
                  <div>pickup {p.booking_config?.pickup_required ? '✓' : '—'} · destination {p.booking_config?.destination_required ? '✓' : '—'} · consent {p.booking_config?.location_consent_required ? '✓' : '—'}</div>
                  <div className="mt-2 text-gray-500 text-xs uppercase">Status workflow</div>
                  <div className="text-xs">{arr(p.status_workflow).join(' → ') || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs uppercase">Scheduling roles</div>
                  {roleCell(p)}
                  <div className="mt-2 text-gray-500 text-xs uppercase">Location / ETA</div>
                  <div className="text-xs" data-testid={`ip-eta-${p.id}`}>
                    enabled {p.location_eta_config?.enabled ? '✓' : '—'} · navigation {p.location_eta_config?.navigation_enabled ? '✓' : '—'} · retention {p.location_eta_config?.retention_days ?? '—'}d
                    <div className="text-amber-700">provider: {p.location_eta_config?.provider || 'none'} — real ETA available only after provider activation (truthful UNAVAILABLE until then).</div>
                    <div data-testid={`ip-sources-${p.id}`}>allowed sources: <b>{allowedSourceLabels(p)}</b></div>
                  </div>
                  <div className="mt-2 text-gray-500 text-xs uppercase">Notifications</div>
                  <div className="text-xs" data-testid={`ip-notif-${p.id}`}>
                    {arr(p.notification_policy?.events).join(', ') || '—'}
                    {/* Shown for EVERY profile, "standard" included, so its absence is legible
                        rather than indistinguishable from the field not existing. */}
                    <div data-testid={`ip-privacy-${p.id}`}>
                      content privacy: <b>{p.notification_policy?.content_privacy || 'standard'}</b>
                    </div>
                  </div>
                  <div className="mt-2 text-gray-500 text-xs uppercase">Display labels</div>
                  <div className="text-xs" data-testid={`ip-labels-${p.id}`}>
                    customer: <b>{p.customer_display_label || '—'}</b> · resource: <b>{p.resource_display_label || '—'}</b>
                  </div>
                  {/* Capture policy (Z10/H5) — READ ONLY. This surface governs the POLICY that says
                      what MAY be captured and whose agreement is needed. It is never a place to grant
                      an individual's consent; that belongs to the person, on their own plane. */}
                  <div className="mt-2 text-gray-500 text-xs uppercase">Capture policy</div>
                  <div className="text-xs" data-testid={`ip-capture-${p.id}`}>
                    {Object.keys(p.capture_policy?.types || {}).length === 0
                      ? <span>none declared — no capture permitted</span>
                      : Object.entries(p.capture_policy.types).map(([kind, cp]) => (
                          <div key={kind} data-testid={`ip-capture-${p.id}-${kind}`}>
                            {kind}: <b>{cp?.enabled ? 'enabled' : 'disabled'}</b>
                            {' · consent '}<b>{cp?.requires_consent === false ? 'not required' : (arr(cp?.consent_parties).join(' + ') || 'unsatisfiable — no party named')}</b>
                            {' · mode '}<b>{cp?.consent_mode || 'explicit'}</b>
                            {cp?.consent_ttl_seconds ? <span> · expires after {cp.consent_ttl_seconds}s</span> : null}
                          </div>
                        ))}
                  </div>
                  <div className="mt-2 text-gray-500 text-xs uppercase">Audit</div>
                  <div className="text-xs">v{p.version} · updated {p.updated_at ? String(p.updated_at).slice(0, 10) : '—'} by {p.updated_by || '—'}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t">
                {/* Not "Edit" - a published version is immutable, so this authors the NEXT one. */}
                <Button size="sm" variant="outline" disabled={busyId === p.id}
                        onClick={() => (verFor?.id === p.id ? closeVersionEditor() : openVersionEditor(p))}
                        data-testid={`ip-newversion-${p.id}`}>
                  {verFor?.id === p.id ? 'Cancel' : 'Create New Version'}
                </Button>
                {active(p)
                  ? <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => setLifecycle(p, 'suspended')} data-testid={`ip-suspend-${p.id}`}>Suspend</Button>
                  : <Button size="sm" disabled={busyId === p.id} onClick={() => setLifecycle(p, 'active')} data-testid={`ip-activate-${p.id}`}>Activate</Button>}
                {/* Governed taxonomy selection. The owner never has to discover or copy a UUID; the
                    canonical ids are submitted internally. Sub-sectors are constrained to the chosen
                    Industry. */}
                <select className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm w-56"
                        value={pendingIndustry(p)} onChange={(e) => setIndustryFor(p, e.target.value)}
                        data-testid={`ip-map-industry-${p.id}`}>
                  <option value="">— Industry —</option>
                  {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
                <select className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm w-56"
                        value={pendingSubs(p)[0] || ''} onChange={(e) => setSubFor(p, e.target.value)}
                        disabled={!pendingIndustry(p)} data-testid={`ip-map-subs-${p.id}`}>
                  <option value="">— All sub-sectors —</option>
                  {subOptionsFor(p).map((sx) => <option key={sx.id} value={sx.id}>{sx.name}</option>)}
                </select>
                <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => assignMapping(p)} data-testid={`ip-map-apply-${p.id}`}>Assign mapping</Button>
              </div>

              {verFor?.id === p.id && verDraft ? (
                <div className="mt-3 p-3 border rounded bg-gray-50" data-testid={`ip-vered-${p.id}`}>
                  <div className="text-sm font-semibold">Create new version of {p.profile_key}</div>
                  <div className="text-xs text-amber-700 mb-2" data-testid={`ip-vered-immutable-${p.id}`}>
                    v{p.version} is immutable and will NOT be modified. Saving publishes v{Number(p.version || 1) + 1};
                    anything you do not change is inherited from v{p.version}, including its Industry/Sub-Sector mapping.
                  </div>
                  {['settings_roles', 'usage_roles', 'confirm_roles'].map((field) => (
                    <div key={field} className="mb-2">
                      <div className="text-xs uppercase text-gray-500">{field.replace('_', ' ')}</div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {ROLE_VOCAB.map((r) => (
                          <label key={r} className="text-xs flex items-center gap-1">
                            <input type="checkbox" checked={(verDraft[field] || []).includes(r)}
                                   onChange={() => toggleRole(field, r)}
                                   data-testid={`ip-vered-${field}-${r}`} />
                            {r}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* GENERIC workflow-stage authoring. Nothing here names an industry, a profile key
                      or a status: the vocabulary comes from the backend that enforces it, and the
                      order shown is the order the transition rule will read. */}
                  <div className="mb-3" data-testid="ip-vered-workflow">
                    <div className="text-xs uppercase text-gray-500">status workflow</div>
                    <div className="text-[11px] text-gray-500 mb-1">
                      Order is the lifecycle - transitions run forward along this list. Cancellation is
                      granted from any active stage by the engine and is never declared here.
                    </div>
                    {wfDraft().length === 0 ? (
                      <div className="text-xs text-gray-600" data-testid="ip-vered-wf-empty">
                        No workflow declared on v{p.version}.
                      </div>
                    ) : (
                      <ol className="text-xs mt-1">
                        {wfDraft().map((st, i) => (
                          <li key={st} className="flex items-center gap-2 py-0.5"
                              data-testid={`ip-vered-wf-row-${st}`}>
                            <span className="w-5 text-gray-400 tabular-nums">{i + 1}.</span>
                            <span className="font-mono">{st}</span>
                            <button type="button" className="px-1 border rounded disabled:opacity-30"
                                    disabled={i === 0} onClick={() => wfMove(i, -1)}
                                    aria-label={`Move ${st} earlier`}
                                    data-testid={`ip-vered-wf-up-${st}`}>&uarr;</button>
                            <button type="button" className="px-1 border rounded disabled:opacity-30"
                                    disabled={i === wfDraft().length - 1} onClick={() => wfMove(i, 1)}
                                    aria-label={`Move ${st} later`}
                                    data-testid={`ip-vered-wf-down-${st}`}>&darr;</button>
                            <button type="button" className="px-1 border rounded text-red-600"
                                    onClick={() => wfRemove(st)} aria-label={`Remove ${st}`}
                                    data-testid={`ip-vered-wf-remove-${st}`}>&times;</button>
                          </li>
                        ))}
                      </ol>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <select className="border rounded px-2 py-1 text-xs" defaultValue=""
                              onChange={(e) => { wfAdd(e.target.value); e.target.value = ''; }}
                              data-testid="ip-vered-wf-add">
                        <option value="">- add a stage -</option>
                        {wfAvailable().map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                      {statusVocab.length === 0 ? (
                        <span className="text-[11px] text-amber-700" data-testid="ip-vered-wf-novocab">
                          Vocabulary unavailable - stages cannot be added right now.
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {/* GENERIC display terminology. The profile owns the word a surface uses for the
                      person delivering the service; the engine never reads it. Blank inherits. */}
                  <div className="mb-3" data-testid="ip-vered-rdl">
                    <div className="text-xs uppercase text-gray-500">resource display label</div>
                    <div className="text-[11px] text-gray-500 mb-1">
                      What surfaces call the person delivering the service. Configuration only - no
                      engine reads it, and no booking changes.
                    </div>
                    <input type="text" className="border rounded px-2 py-1 text-xs w-64"
                           value={verDraft.resource_display_label}
                           onChange={(e) => setVerDraft((d) => ({ ...d, resource_display_label: e.target.value }))}
                           data-testid="ip-vered-rdl-input" />
                  </div>

                  {/* GENERIC capture policy. Declares what MAY be captured and whose agreement is
                      required. It grants nothing: an individual's consent is theirs to give, on their
                      own plane, and is never authored here. An absent or disabled type permits no
                      capture at all - silence is a refusal, not a default. */}
                  <div className="mb-3" data-testid="ip-vered-capture">
                    <div className="text-xs uppercase text-gray-500">capture policy</div>
                    <div className="text-[11px] text-gray-500 mb-1">
                      What may be captured, and whose explicit agreement is required first. This sets
                      POLICY only - nobody's consent is recorded here.
                    </div>
                    {CAPTURE_TYPES.map((kind) => {
                      const row = verDraft.capture_types[kind];
                      return (
                        <div key={kind} className="flex flex-wrap items-center gap-2 text-xs mb-1"
                             data-testid={`ip-vered-capture-${kind}`}>
                          <label className="inline-flex items-center gap-1 w-24">
                            <input type="checkbox" checked={row.enabled}
                                   onChange={() => toggleCaptureType(kind)}
                                   data-testid={`ip-vered-capture-${kind}-enabled`} />
                            <span>{kind}</span>
                          </label>
                          {row.enabled ? (
                            <>
                              <span className="text-gray-500">consent from:</span>
                              {CONSENT_PARTIES.map((party) => (
                                <label key={party} className="inline-flex items-center gap-1">
                                  <input type="checkbox" checked={row.parties.includes(party)}
                                         onChange={() => toggleCaptureParty(kind, party)}
                                         data-testid={`ip-vered-capture-${kind}-party-${party}`} />
                                  <span>{party}</span>
                                </label>
                              ))}
                              <span className="text-gray-500">mode <b>{CONSENT_MODE}</b></span>
                              {row.parties.length === 0 ? (
                                <span className="text-amber-700"
                                      data-testid={`ip-vered-capture-${kind}-unsatisfiable`}>
                                  no party named - this would permit no capture at all
                                </span>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {/* GENERIC preview-content policy. Governs what a notification may say OUTSIDE the
                      authenticated app; it changes no stored message and no in-app screen. */}
                  <div className="mb-3" data-testid="ip-vered-privacy">
                    <div className="text-xs uppercase text-gray-500">notification content privacy</div>
                    <div className="text-[11px] text-gray-500 mb-1">
                      Governs push and SMS previews only - what appears on a lock screen. Stored
                      messages, in-app screens and delivery itself are unchanged.
                    </div>
                    <select className="border rounded px-2 py-1 text-xs"
                            value={verDraft.content_privacy}
                            onChange={(e) => setVerDraft((d) => ({ ...d, content_privacy: e.target.value }))}
                            data-testid="ip-vered-privacy-select">
                      {CONTENT_PRIVACY_VALUES.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <span className="text-[11px] text-gray-500 ml-2"
                          data-testid="ip-vered-privacy-events">
                      inherits {arrOf(verDraft.notification_events).length} notification event(s)
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="text-xs uppercase text-gray-500">confirm scope</div>
                    <select className="border rounded px-2 py-1 text-xs mt-1" value={verDraft.confirm_scope}
                            onChange={(e) => setVerDraft((d) => ({ ...d, confirm_scope: e.target.value }))}
                            data-testid="ip-vered-scope">
                      {CONFIRM_SCOPES.map((sc) => <option key={sc} value={sc}>{sc}</option>)}
                    </select>
                  </div>
                  {!verReview ? (
                    <Button size="sm" onClick={() => setVerReview(true)} data-testid="ip-vered-review">Review Changes</Button>
                  ) : (
                    <div data-testid="ip-vered-diff">
                      <div className="text-xs mb-1">
                        FROM <b>{p.profile_key} v{p.version}</b> to <b>{p.profile_key} v{Number(p.version || 1) + 1}</b>
                      </div>
                      {versionDiff().length === 0 ? (
                        <div className="text-xs text-gray-600" data-testid="ip-vered-nochange">
                          No changes - nothing would differ from v{p.version}. Create is disabled.
                        </div>
                      ) : (
                        <ul className="text-xs list-disc ml-5">
                          {versionDiff().map((d) => (<li key={d.field}><b>{d.field}</b>: {d.before} to {d.after}</li>))}
                        </ul>
                      )}
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" disabled={busyId === p.id || versionDiff().length === 0}
                                onClick={createVersion} data-testid="ip-vered-create">
                          Create v{Number(p.version || 1) + 1}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setVerReview(false)} data-testid="ip-vered-back">Back</Button>
                        <Button size="sm" variant="outline" onClick={closeVersionEditor} data-testid="ip-vered-cancel">Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default IndustryProfileManager;
